/**
 * Générateur — Automatisme : connaitre et reconnaitre les carrés parfaits
 * des entiers de 0 à 12.
 * Attendu ID : 1101 | Niveau : 4ème | Domaine : Puissances
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 675 :
 * « Connaitre et reconnaitre les carrés parfaits des entiers de 0 à 12. »).
 * Sert AUSSI l'id 1105 (Racine carrée, l. 691 « Donner les carrés des
 * nombres entiers compris entre 0 et 12 ») par le wrapper gen_1105, qui ne
 * prend que les clés `donner_*` (dispatch par clé, modèle 1103/1104).
 *
 * ── Arbitrage prof (2026-09-12, épreuve de Musk sur les deux intitulés) ──
 * Même savoir (les treize carrés de 0 à 12), deux VERBES, deux sens de
 * lecture :
 *   - l. 691 « DONNER les carrés » (Racine carrée) : on part de l'entier, on
 *     produit le carré (9 → 81). C'est la récitation que la racine carrée
 *     prend pour socle (mise en œuvre Eduscol 2026 p. 17 : « je cherche le
 *     nombre positif qui, multiplié par lui-même, donne 81 ; or 9 × 9 =
 *     81 ») ; la racine elle-même est l'objectif l. 693. → id 1105, clés
 *     donner_* : flash directe et QCM « Combien vaut 7² ? ».
 *   - l. 675 « connaitre et RECONNAITRE les carrés parfaits » (Puissances) :
 *     on part d'un nombre, on dit s'il est un carré parfait et de quel
 *     entier (49 → 7² ; 50 → non) ; le mot « carré parfait » est le neuf.
 *     → id 1101, clés reconnaitre_* et parfait_* : flash inverse, QCM « 81
 *     est le carré de quel nombre ? », V/F « Carré parfait ou pas ? ».
 * Aucune carte en commun ; les deux ids sont de la même année, l'élève a
 * les deux sens. MathsMentales (4NC1, même entrée pour les deux ids, sens
 * direct « a × a », « a² » ; option « Racines de 1 à 144 » sans niveau)
 * ne fait pas la distinction : l'arbitrage suit les verbes du programme.
 * Le wrapper de 927 (5e, objectif à quatre cartes) reste rejeté.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   1105 « Donner le carré » (donner_texte_trou_saisie) : quatre lignes
 *     « 7² = … », dont une base 11 ou 12 (hors tables de 1047) et une base
 *     0 ou 1 une fois sur six (1² = 1, pas 2 ; À ARBITRER).
 *   1105 « Combien vaut 7² ? » (donner_qcm_unique) : base 3..12, QUATRE choix
 *     14 < 42 < 49 < 56, la clé encadrée (a = 2 exclu : 2 × 2 = 2² = 4).
 *   1101 « Retrouver l'entier » (reconnaitre_texte_trou_saisie) : quatre
 *     lignes « …² = 121 » ou « 121 est le carré de … », bases 2..12 (0 et 1
 *     rares), dont une base 11 ou 12.
 *   1101 « 81 est le carré de… » (reconnaitre_qcm_unique) : base 2..12,
 *     choix 9, 8, 10 (carre_voisin), 40,5 (moitie) ou 3 (racine_de_racine).
 *   1101 « Carré parfait ou pas ? » (parfait_tableau_vf) : quatre lignes,
 *     une par gabarit : « 121 est un carré parfait » (V), « 50 est un carré
 *     parfait » (F, presque_carre), « 49 est le carré de 7 » (V), « 64 est
 *     le carré de 9 » (F, carre_voisin).
 *
 * INVARIANTS : bases 0..12 STRICTEMENT (périmètre des variables du
 * programme et de la banque, option « Carrés de 1 à 12 ») ; non-carrés
 * pièges de gen_139 (carré ± 1, doubles, cubes) sous 145 ; chaque égalité
 * re-vérifiée (throw).
 * SACRIFICE : aucun. L'exemple de la fiche à venir est 13² = 169, HORS PLAGE
 * (choix mode auto, À ARBITRER contre 6² = 36 sacrifié).
 *
 * ── Distracteurs (familles nommées) ─────────────────────────────────────
 *   base_fois_exposant : 7² → 14, LA confusion (gen_926, 928), sens direct ;
 *   voisin_meme_table  : 7² → 42 ou 56 (nom de 1047) ;
 *   carre_voisin       : (a ± 1)² : 7² → 36 ou 64 ; sens inverse 49 → 6 ou 8 ;
 *   moitie             : sens inverse, le double défait : 64 → 32 ;
 *                        49 → 24,5 (gen_927) ;
 *   racine_de_racine   : 16 → 2, 81 → 3 (gen_929) ;
 *   presque_carre      : 48, 50, 14, 8, 27 pris pour des carrés (reconnaitre).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1101;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DONNER = 'Donner le carré';
const L_DONNER_QCM = 'Combien vaut 7² ?';
const L_RECONNAITRE = 'Retrouver l\'entier';
const L_RECONNAITRE_QCM = '81 est le carré de…';
const L_VF = 'Carré parfait ou pas ?';
const L_PARFAITS = 'Lesquels sont des carrés parfaits ?';

const A_MAX = 12;

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Non-carrés pièges (gen_139) : carré ± 1, doubles de carrés, cubes, sous 12² + 1. */
export const NON_CARRES = [8, 14, 18, 24, 27, 44, 48, 50, 63, 65, 72, 80, 82, 99, 101, 110, 120, 122, 125, 128, 140, 143, 145];

const carre = (a) => {
    if (!Number.isInteger(a) || a < 0 || a > A_MAX) throw new Error(`gen_1101 : base hors domaine ${a}`);
    const c = a * a;
    if (Math.sqrt(c) !== a) throw new Error('gen_1101 : arithmétique');
    return c;
};

/** Une base : 0 ou 1 une fois sur six si `rare`, sinon dans max(min, 2)..max. */
const tirerBase = ({ min = 0, max = A_MAX, rare = true } = {}) => {
    if (rare && min <= 1 && randInt(1, 6) === 1) return randInt(Math.max(min, 0), 1);
    return randInt(Math.max(min, 2), max);
};

const EXPLICATIONS = {
    base_fois_exposant: '7², c\'est 7 × 7, pas 7 × 2. Le petit 2 dit combien de fois le 7 est dans le produit.',
    voisin_meme_table: 'C\'est un résultat de la table de 7, mais pas 7 × 7.',
    carre_voisin: 'C\'est le carré du nombre d\'à côté : 6 × 6 = 36, 8 × 8 = 64, et 7 × 7 = 49.',
    moitie: 'Le carré n\'est pas un double : 64, c\'est 8 × 8, pas 32 × 2.',
    racine_de_racine: '81 est le carré de 9 (9 × 9 = 81) ; 3 est le carré de… non, 3² = 9.',
    presque_carre: '50 n\'est pas un carré parfait : 7² = 49 et 8² = 64, rien entre les deux.',
    regle: 'Le carré d\'un nombre, c\'est le nombre multiplié par lui-même : 7² = 7 × 7 = 49. Un carré parfait, c\'est le carré d\'un entier.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille du gen (catalogue validé des codes d'erreur) ; la moitié prise pour la racine et les carrés voisins ont leurs propres codes depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md). */
const CODE_COMMUN = {
    base_fois_exposant: 'base_fois_exposant',
    // Relecture du 04/10/2026 (E09) : 42 pour 7², c'est 6 × 7, un geste de table, pas un carré voisin : sans nom.
    voisin_meme_table: null,
    carre_voisin: 'carre_voisin',
    moitie: 'racine_lue_comme_moitie',
    racine_de_racine: 'racine_de_racine',
    presque_carre: 'presque_carre',
};

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre (24,5 = 24.50), son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Les leurres d'un QCM ({ valeur, code du gen }) dont l'erreur est nommable : le texte exact du choix, son code commun. */
const erreursQcm = (leurres, nommees) => leurres
    .map((l) => ({ code: CODE_COMMUN[l.code], valeur: String(l.valeur) }))
    .filter((l) => l.code && nommees.some((e) => e.code === l.code && cleReponse(e.valeur) === cleReponse(l.valeur)));

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** « a² = … » : 2a, a + 2 ; bases 3 à 12 (le QCM) : a(a ± 1) comme le QCM le construit (jamais 3 × 2, qui est 2a), (a ± 1)². */
const famillesDirect = (a) => {
    const f = [{ code: 'base_fois_exposant', valeur: 2 * a }, { code: 'base_plus_exposant', valeur: a + 2 }];
    if (a >= 3) {
        [a * (a - 1), a * (a + 1)].filter((v) => v !== 2 * a).forEach((v) => f.push({ code: null, valeur: v }));
        [(a - 1) ** 2, (a + 1) ** 2].forEach((v) => f.push({ code: 'carre_voisin', valeur: v }));
    }
    return f;
};

/** « …² = a² » : 2, l'exposant donné pour la base ; bases 3 à 12 (le QCM) : a ± 1, la moitié de a², ou la racine de a si a est un carré. */
const famillesInverse = (a) => {
    const c = a * a;
    const f = [{ code: 'exposant_au_lieu_de_la_base', valeur: '2' }];
    if (a >= 3) {
        f.push({ code: 'carre_voisin', valeur: String(a - 1) }, { code: 'carre_voisin', valeur: String(a + 1) });
        f.push(Number.isInteger(Math.sqrt(a))
            ? { code: 'racine_de_racine', valeur: String(Math.sqrt(a)) }
            : { code: 'racine_lue_comme_moitie', valeur: c % 2 === 0 ? String(c / 2) : `${(c - 1) / 2},5` });
    }
    return f;
};

/** Vrai/faux : chaque ligne fausse sous son code, sauf « 9 est le carré de 2 », où 2 est aussi l'exposant. */
const erreursVf = (items) => items.filter((it) => it.code).flatMap((it) => {
    const code = CODE_COMMUN[it.code];
    if (it.nom !== 'inverse_fausse') return [{ code, ligne: it.ligne }];
    const donne = (it.ligne.match(/de (\d+)\.$/) || [])[1];
    return nommer(famillesInverse(it.a), String(it.a)).some((e) => e.code === code && cleReponse(e.valeur) === cleReponse(donne))
        ? [{ code, ligne: it.ligne }] : [];
});

// ─── Séries flash (KaTeX, un entier par ligne) ────────────────────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b></p>';

const ligneDirecte = (a) => `$${a}^2 = {{${carre(a)}}}$`;
const ligneInverse = (a) => (randomChoice([true, false])
    ? `${'$'}{{${a}}}^2 = ${carre(a)}$`
    : `$${carre(a)} \\text{ est le carré de } {{${a}}}$`);

const assemblerFlash = (bases, ligne, label, erreursDe) => {
    const payload = makeTexteTrou(bases.map(ligne).join('\n'), CONSIGNE_FLASH, { ...opts(label), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    // une ligne, un trou : les erreurs de chaque base à la place de son trou
    ranger(payload, bases.flatMap((a, trou) => erreursDe(a).map(({ code, valeur }) => ({ code, trou, valeur }))));
    payload._meta.params = { bases };
    return payload;
};

/** Quatre bases distinctes : une 11 ou 12, une rare (0, 1) possible, deux de table. */
const tirerBases = (minRare) => {
    for (let essai = 0; essai < 80; essai++) {
        const bases = [randInt(11, 12), tirerBase({ min: 2, max: 10, rare: false }), tirerBase({ min: 2, max: 10, rare: false }), tirerBase({ min: minRare, max: 10 })];
        if (new Set(bases).size === 4) return shuffle(bases);
    }
    return [12, 7, 9, 3];
};

/** 1105 : le sens direct, 7² = … (0 et 1 admis, rares). */
const genFlashDonner = () => assemblerFlash(tirerBases(0), ligneDirecte, L_DONNER, (a) => nommer(famillesDirect(a), String(carre(a))));
/** 1101 : le sens inverse, …² = 49 / 49 est le carré de … (0 et 1 admis, rares : 0² = 0 et 1² = 1 se reconnaissent aussi). */
const genFlashReconnaitre = () => assemblerFlash(tirerBases(0), ligneInverse, L_RECONNAITRE, (a) => nommer(famillesInverse(a), String(a)));

// ─── QCM, un par sens ─────────────────────────────────────────────────────

const distracteursDirect = (a) => {
    const c = carre(a);
    const cands = [
        { valeur: 2 * a, code: 'base_fois_exposant' },
        { valeur: a * (a + randomChoice([-1, 1])), code: 'voisin_meme_table' },
        { valeur: carre(a + (a === A_MAX ? -1 : randomChoice([-1, 1]))), code: 'carre_voisin' },
    ];
    const vals = new Set([c, ...cands.map((x) => x.valeur)]);
    return vals.size === 4 && cands.every((x) => x.valeur > 0) ? cands : null;
};

const distracteursInverse = (a) => {
    const c = carre(a);
    const cands = [
        { valeur: String(a - 1), code: 'carre_voisin' },
        { valeur: String(a + 1), code: 'carre_voisin' },
        c % 2 === 0 ? { valeur: String(c / 2), code: 'moitie' } : { valeur: `${(c - 1) / 2},5`, code: 'moitie' },
    ];
    // 16 → 2 et 81 → 3 : la racine de la racine remplace la moitié
    if (Number.isInteger(Math.sqrt(a)) && a > 1) cands[2] = { valeur: String(Math.sqrt(a)), code: 'racine_de_racine' };
    const vals = new Set([String(a), ...cands.map((x) => x.valeur)]);
    return vals.size === 4 ? cands : null;
};

/** 1105 : « Combien vaut 7² ? », base 3..12. */
const genQcmDonner = () => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(3, A_MAX);
        const faux = distracteursDirect(a);
        if (!faux) continue;
        const bon = String(carre(a));
        const payload = makeQcm([bon, ...faux.map((x) => String(x.valeur))], bon, `<p><b>Combien vaut ${tex(`${a}^2`)} ?</b></p>`, opts(L_DONNER_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursQcm(faux, nommer(famillesDirect(a), bon)));
        payload._meta.params = { sens: 'direct', a, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const payload = makeQcm(['49', '14', '42', '64'], '49', `<p><b>Combien vaut ${tex('7^2')} ?</b></p>`, opts(L_DONNER_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursQcm([{ valeur: 14, code: 'base_fois_exposant' }, { valeur: 42, code: 'voisin_meme_table' }, { valeur: 64, code: 'carre_voisin' }], nommer(famillesDirect(7), '49')));
    payload._meta.params = { sens: 'direct', a: 7, distracteurs: [{ code: 'base_fois_exposant' }, { code: 'voisin_meme_table' }, { code: 'carre_voisin' }] };
    return payload;
};

/** 1101 : « 81 est le carré de quel nombre ? », base 2..12. */
const genQcmReconnaitre = () => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(2, A_MAX);
        const faux = distracteursInverse(a);
        if (!faux) continue;
        const bon = String(a);
        // deux formulations : la nôtre et celle de la mise en œuvre Eduscol 2026 (« le nombre qui, multiplié par lui-même, donne 81 »)
        const enonce = randomChoice([
            `${carre(a)} est le carré de quel nombre ?`,
            `Quel entier, multiplié par lui-même, donne ${carre(a)} ?`,
        ]);
        const payload = makeQcm([bon, ...faux.map((x) => x.valeur)], bon, `<p><b>${enonce}</b></p>`, opts(L_RECONNAITRE_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursQcm(faux, nommer(famillesInverse(a), bon)));
        payload._meta.params = { sens: 'inverse', a, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const payload = makeQcm(['9', '8', '10', '3'], '9', '<p><b>81 est le carré de quel nombre ?</b></p>', opts(L_RECONNAITRE_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursQcm(distracteursInverse(9), nommer(famillesInverse(9), '9')));
    payload._meta.params = { sens: 'inverse', a: 9, distracteurs: [{ code: 'carre_voisin' }, { code: 'carre_voisin' }, { code: 'racine_de_racine' }] };
    return payload;
};

// ─── 1101 : QCM multiple « Lesquels sont des carrés parfaits ? » (5 propositions) ─
// Demande prof 2026-09-12 : cinq nombres à trier, deux ou trois carrés parfaits
// (de 0 à 12, 0 et 1 rares) et le reste pris parmi les non-carrés pièges.
// Croisements : C(13, 2..3) × C(23, 2..3) tirages, bien au-delà de la dizaine
// de variantes demandée, sans sortir du périmètre 0..12.

const genQcmMultiple = () => {
    for (let essai = 0; essai < 80; essai++) {
        const nbCarres = randomChoice([2, 3]);
        const bases = new Set();
        while (bases.size < nbCarres) bases.add(tirerBase({ min: 0 }));
        const carres = [...bases].map(carre);
        const faux = shuffle(NON_CARRES).slice(0, 5 - nbCarres);
        const choix = [...carres, ...faux].map(String);
        if (new Set(choix).size !== 5) continue;
        const payload = makeQcm(choix, carres.map(String), '<p><b>Parmi ces nombres, lesquels sont des carrés parfaits ?</b> Coche tous ceux qui le sont.</p>', opts(L_PARFAITS), true);
        payload.contenu = { ...payload.contenu, explications: { presque_carre: EXPLICATIONS.presque_carre, regle: EXPLICATIONS.regle } };
        ranger(payload, faux.map((n) => ({ code: CODE_COMMUN.presque_carre, valeur: String(n) })));
        payload._meta.params = { bases: [...bases], faux, distracteurs: faux.map(() => ({ code: 'presque_carre' })) };
        return payload;
    }
    const payload = makeQcm(['49', '121', '50', '14', '8'], ['49', '121'], '<p><b>Parmi ces nombres, lesquels sont des carrés parfaits ?</b> Coche tous ceux qui le sont.</p>', opts(L_PARFAITS), true);
    payload.contenu = { ...payload.contenu, explications: { presque_carre: EXPLICATIONS.presque_carre, regle: EXPLICATIONS.regle } };
    ranger(payload, ['50', '14', '8'].map((valeur) => ({ code: CODE_COMMUN.presque_carre, valeur })));
    payload._meta.params = { bases: [7, 11], faux: [50, 14, 8], distracteurs: [{ code: 'presque_carre' }, { code: 'presque_carre' }, { code: 'presque_carre' }] };
    return payload;
};

// ─── 1101 : Vrai ou faux « Carré parfait ou pas ? » (4 lignes, une par gabarit) ─

const GABARITS = {
    parfait_vrai: () => { const a = tirerBase({ min: 2 }); return { ligne: `${carre(a)} est un carré parfait.`, vrai: true, a }; },
    parfait_faux: () => { const n = randomChoice(NON_CARRES); return { ligne: `${n} est un carré parfait.`, vrai: false, code: 'presque_carre', n }; },
    inverse_vraie: () => { const a = tirerBase({ min: 2 }); return { ligne: `${carre(a)} est le carré de ${a}.`, vrai: true, a }; },
    inverse_fausse: () => { const a = randInt(3, A_MAX); return { ligne: `${carre(a)} est le carré de ${a + randomChoice([-1, 1])}.`, vrai: false, code: 'carre_voisin', a }; },
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const items = shuffle(Object.keys(GABARITS)).map((nom) => ({ nom, ...GABARITS[nom]() }));
        if (new Set(items.map((it) => it.ligne)).size < 4) continue;
        if (new Set(items.map((it) => it.a ?? `n${it.n}`)).size < 4) continue;
        const payload = makeVraiFaux(items.map((it) => it.ligne), items.map((it) => (it.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursVf(items));
        payload._meta.params = { items: items.map(({ nom, vrai, code, a, n }) => ({ nom, vrai, ...(code ? { code } : {}), ...(a !== undefined ? { a } : { n }) })) };
        return payload;
    }
    const lignes = ['121 est un carré parfait.', '50 est un carré parfait.', '49 est le carré de 7.', '64 est le carré de 9.'];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursVf([{ nom: 'parfait_faux', code: 'presque_carre', ligne: lignes[1] }, { nom: 'inverse_fausse', code: 'carre_voisin', a: 8, ligne: lignes[3] }]));
    payload._meta.params = { items: [{ nom: 'parfait_vrai', vrai: true, a: 11 }, { nom: 'parfait_faux', vrai: false, code: 'presque_carre', n: 50 }, { nom: 'inverse_vraie', vrai: true, a: 7 }, { nom: 'inverse_fausse', vrai: false, code: 'carre_voisin', a: 8 }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix distincts ; sens direct (1105) : 2a < a(a ± 1) < a² < (a + 1)², la clé encadrée ; sens inverse (1101) : a − 1 < a < a + 1, plus la moitié ou la racine de la racine',
        'a = 2 exclu du sens direct (2 × 2 = 2² : le double serait la clé)',
        'aucun sacrifice : l\'exemple de la fiche, 13² = 169, est hors plage',
    ],
    donner_qcm_unique: {
        support: '« Combien vaut a² ? », a 3..12',
        familles: {
            base_fois_exposant: '2a : 7² → 14, la confusion principale (gen_926, gen_928)',
            voisin_meme_table: 'a(a ± 1) : 7² → 42 ou 56 (nom de 1047)',
            carre_voisin: '(a ± 1)² : 7² → 36 ou 64',
        },
    },
    reconnaitre_qcm_unique: {
        support: '« a² est le carré de quel nombre ? », a 2..12',
        familles: {
            carre_voisin: 'a − 1 et a + 1 : 49 → 6 ou 8',
            moitie: 'le double défait : 64 → 32, 49 → 24,5 (gen_927)',
            racine_de_racine: '16 → 2, 81 → 3 (gen_929), à la place de la moitié quand la base est elle-même un carré',
        },
    },
    vf_tableau_vf: 'quatre lignes, une par gabarit : carré parfait vrai / faux (non-carrés pièges de gen_139), sens inverse juste / voisin',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    reconnaitre_texte_trou_saisie: genFlashReconnaitre,
    reconnaitre_qcm_unique: genQcmReconnaitre,
    parfaits_qcm_multiple: genQcmMultiple,
    parfait_tableau_vf: genVf,
    donner_texte_trou_saisie: genFlashDonner,
    donner_qcm_unique: genQcmDonner,
};

/** 1101 : reconnaître. */
export const CATEGORIES = [
    { id: 'reconnaitre', label: L_RECONNAITRE, formatsList: [{ key: 'reconnaitre_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'reconnaitre_qcm', label: L_RECONNAITRE_QCM, formatsList: [{ key: 'reconnaitre_qcm_unique', label: 'QCM' }] },
    { id: 'parfaits', label: L_PARFAITS, formatsList: [{ key: 'parfaits_qcm_multiple', label: 'QCM multiple' }] },
    { id: 'parfait', label: L_VF, formatsList: [{ key: 'parfait_tableau_vf', label: 'Vrai/Faux' }] },
];

/** 1105 : donner (clés donner_*). */
export const CATEGORIES_DONNER = [
    { id: 'donner', label: L_DONNER, formatsList: [{ key: 'donner_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'donner_qcm', label: L_DONNER_QCM, formatsList: [{ key: 'donner_qcm_unique', label: 'QCM' }] },
];

const CLES_1101 = Object.keys(GENERATORS).filter((k) => !k.startsWith('donner_'));

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    reconnaitre_qcm_unique: ['racine_de_racine'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = CLES_1101.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(CLES_1101);
    } else key = randomChoice(CLES_1101);
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
